import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2 } from 'lucide-react';
import {
  EMPLOYEE_EVENT_TYPES,
  SERVICE_EVENT_TYPES,
  EMPLOYEE_DURATION_PRESETS,
  SERVICE_DURATION_PRESETS,
} from '@/types/schedule';
import { adminDialogPanel, adminOverlay } from '@/components/admin/adminUi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function toLocalDatetimeInput(date) {
  if (!date) return '';
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function buildIsoFromLocalInput(value) {
  if (!value) return '';
  return new Date(value).toISOString();
}

export default function ScheduleEventModal({
  open,
  onClose,
  onSaved,
  calendarType,
  token,
  editEvent = null,
  initialStart = null,
}) {
  const eventTypeOptions = calendarType === 'employee' ? EMPLOYEE_EVENT_TYPES : SERVICE_EVENT_TYPES;
  const durationOptions = calendarType === 'employee' ? EMPLOYEE_DURATION_PRESETS : SERVICE_DURATION_PRESETS;

  const [saving, setSaving] = useState(false);
  const [leads, setLeads] = useState([]);
  const [deals, setDeals] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [form, setForm] = useState({
    event_type: eventTypeOptions[0]?.value || '',
    title: '',
    description: '',
    start_at: '',
    duration_preset: durationOptions[0]?.value || '2h',
    duration_minutes: 120,
    lead_id: '',
    lead_type: '',
    deal_id: '',
    service_request_id: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    vehicle_info: '',
  });

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const selectedTypeMeta = eventTypeOptions.find((t) => t.value === form.event_type) || {};

  useEffect(() => {
    if (!open) return;
    if (editEvent) {
      const links = editEvent.links || {};
      setForm({
        event_type: editEvent.event_type,
        title: editEvent.title || '',
        description: editEvent.description || '',
        start_at: toLocalDatetimeInput(editEvent.start_at),
        duration_preset: editEvent.duration_preset || 'custom',
        duration_minutes: editEvent.duration_minutes || 60,
        lead_id: links.lead_id || '',
        lead_type: links.lead_type || '',
        deal_id: links.deal_id || '',
        service_request_id: links.service_request_id || '',
        customer_name: links.customer_name || '',
        customer_phone: links.customer_phone || '',
        customer_email: links.customer_email || '',
        vehicle_info: links.vehicle_info || '',
      });
    } else {
      setForm({
        event_type: eventTypeOptions[0]?.value || '',
        title: '',
        description: '',
        start_at: toLocalDatetimeInput(initialStart || new Date()),
        duration_preset: durationOptions[0]?.value || '2h',
        duration_minutes: calendarType === 'employee' ? 60 : 120,
        lead_id: '',
        lead_type: '',
        deal_id: '',
        service_request_id: '',
        customer_name: '',
        customer_phone: '',
        customer_email: '',
        vehicle_info: '',
      });
    }
  }, [open, editEvent, initialStart, calendarType, eventTypeOptions, durationOptions]);

  useEffect(() => {
    if (!open || !token) return;
    const load = async () => {
      setLoadingOptions(true);
      try {
        const reqs = [axios.get(`${BACKEND_URL}/api/admin/schedule/link-options/leads`, { headers })];
        if (calendarType === 'employee') {
          reqs.push(
            axios.get(`${BACKEND_URL}/api/admin/schedule/link-options/deals`, {
              headers,
              params: { for_delivery: selectedTypeMeta.needsDeliveryDeal || false },
            }),
          );
        } else {
          reqs.push(
            axios.get(`${BACKEND_URL}/api/admin/schedule/link-options/service-requests`, { headers }),
          );
        }
        const results = await Promise.all(reqs);
        setLeads(results[0].data.leads || []);
        if (calendarType === 'employee') {
          setDeals(results[1].data.deals || []);
        } else {
          setServiceRequests(results[1].data.service_requests || []);
        }
      } catch (err) {
        toast.error('Failed to load link options');
      } finally {
        setLoadingOptions(false);
      }
    };
    load();
  }, [open, token, headers, calendarType, selectedTypeMeta.needsDeliveryDeal]);

  useEffect(() => {
    if (!form.lead_id) return;
    const lead = leads.find((l) => l.id === form.lead_id);
    if (!lead) return;
    setForm((prev) => ({
      ...prev,
      lead_type: lead.type,
      customer_name: lead.name || prev.customer_name,
      customer_phone: lead.phone || prev.customer_phone,
      customer_email: lead.email || prev.customer_email,
      vehicle_info: lead.vehicle_info || prev.vehicle_info,
    }));
  }, [form.lead_id, leads]);

  useEffect(() => {
    if (!form.deal_id) return;
    const deal = deals.find((d) => d.id === form.deal_id);
    if (!deal) return;
    setForm((prev) => ({
      ...prev,
      customer_name: deal.customer_name || prev.customer_name,
      vehicle_info: deal.vehicle_info || prev.vehicle_info,
    }));
  }, [form.deal_id, deals]);

  useEffect(() => {
    if (!form.service_request_id) return;
    const req = serviceRequests.find((r) => r.id === form.service_request_id);
    if (!req) return;
    setForm((prev) => ({
      ...prev,
      customer_name: req.customer_name || prev.customer_name,
      vehicle_info: req.vehicle_info || prev.vehicle_info,
    }));
  }, [form.service_request_id, serviceRequests]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const startDate = new Date(form.start_at);
    if (startDate.getDay() === 0) {
      toast.error('Events cannot be scheduled on Sundays');
      return;
    }

    const payload = {
      event_type: form.event_type,
      title: form.title || undefined,
      description: form.description,
      start_at: buildIsoFromLocalInput(form.start_at),
      duration_preset: form.duration_preset,
      duration_minutes: form.duration_preset === 'custom' ? Number(form.duration_minutes) : undefined,
      links: {
        lead_id: form.lead_id || undefined,
        lead_type: form.lead_type || undefined,
        deal_id: form.deal_id || undefined,
        service_request_id: form.service_request_id || undefined,
        customer_name: form.customer_name || undefined,
        customer_phone: form.customer_phone || undefined,
        customer_email: form.customer_email || undefined,
        vehicle_info: form.vehicle_info || undefined,
      },
      source: 'admin',
    };

    setSaving(true);
    try {
      const base = `${BACKEND_URL}/api/admin/schedule/${calendarType}`;
      if (editEvent?.id) {
        await axios.put(`${base}/${editEvent.id}`, payload, { headers });
        toast.success('Event updated');
      } else {
        await axios.post(base, payload, { headers });
        toast.success('Event scheduled');
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className={adminOverlay('z-[70]')}>
      <div className={adminDialogPanel('max-w-2xl')}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h2 className="text-lg font-['Unbounded'] font-semibold text-white">
            {editEvent ? 'Edit Event' : 'New Event'} — {calendarType === 'employee' ? 'Employee' : 'Service'}
          </h2>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-['Manrope'] text-white/50 mb-1">Event Type</label>
            <select
              value={form.event_type}
              onChange={(e) => setForm((p) => ({ ...p, event_type: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope']"
            >
              {eventTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-[#111]">{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-['Manrope'] text-white/50 mb-1">Start</label>
              <input
                type="datetime-local"
                required
                value={form.start_at}
                onChange={(e) => setForm((p) => ({ ...p, start_at: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope']"
              />
            </div>
            <div>
              <label className="block text-xs font-['Manrope'] text-white/50 mb-1">Duration</label>
              <select
                value={form.duration_preset}
                onChange={(e) => setForm((p) => ({ ...p, duration_preset: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope']"
              >
                {durationOptions.map((opt) => (
                  <option key={opt.value} value={opt.value} className="bg-[#111]">{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {form.duration_preset === 'custom' && (
            <div>
              <label className="block text-xs font-['Manrope'] text-white/50 mb-1">Custom Duration (minutes)</label>
              <input
                type="number"
                min={15}
                step={15}
                required
                value={form.duration_minutes}
                onChange={(e) => setForm((p) => ({ ...p, duration_minutes: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope']"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-['Manrope'] text-white/50 mb-1">Title (optional)</label>
            <input
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope']"
              placeholder="Auto-generated from event type if blank"
            />
          </div>

          <div>
            <label className="block text-xs font-['Manrope'] text-white/50 mb-1">Notes / Description</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] resize-none"
            />
          </div>

          {loadingOptions && (
            <p className="text-xs text-white/40 font-['Manrope'] flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading link options…
            </p>
          )}

          {calendarType === 'employee' && selectedTypeMeta.needsLinks && (
            <div className="space-y-3 p-4 bg-white/[0.03] border border-white/10">
              <p className="text-xs font-['Unbounded'] text-white/70">Linked Records</p>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/50 mb-1">Lead</label>
                <select
                  value={form.lead_id}
                  onChange={(e) => setForm((p) => ({ ...p, lead_id: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope']"
                >
                  <option value="" className="bg-[#111]">— Select lead —</option>
                  {leads.map((lead) => (
                    <option key={`${lead.type}-${lead.id}`} value={lead.id} className="bg-[#111]">{lead.label}</option>
                  ))}
                </select>
              </div>
              {(selectedTypeMeta.needsDeliveryDeal || form.event_type === 'vehicle_delivery') && (
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/50 mb-1">
                    Deal {selectedTypeMeta.needsDeliveryDeal ? '(Ready to Close+)' : ''}
                  </label>
                  <select
                    value={form.deal_id}
                    onChange={(e) => setForm((p) => ({ ...p, deal_id: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope']"
                  >
                    <option value="" className="bg-[#111]">— Select deal —</option>
                    {deals.map((deal) => (
                      <option key={deal.id} value={deal.id} className="bg-[#111]">
                        {deal.label || `${deal.customer_name} (${deal.status})`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {calendarType === 'service' && (
            <div className="space-y-3 p-4 bg-white/[0.03] border border-white/10">
              <p className="text-xs font-['Unbounded'] text-white/70">Service Request</p>
              <select
                value={form.service_request_id}
                onChange={(e) => setForm((p) => ({ ...p, service_request_id: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope']"
              >
                <option value="" className="bg-[#111]">— Select service request —</option>
                {serviceRequests.map((req) => (
                  <option key={req.id} value={req.id} className="bg-[#111]">{req.label}</option>
                ))}
              </select>
            </div>
          )}

          {(selectedTypeMeta.needsLinks || calendarType === 'service') && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                placeholder="Customer name"
                value={form.customer_name}
                onChange={(e) => setForm((p) => ({ ...p, customer_name: e.target.value }))}
                className="bg-white/5 border border-white/10 text-white text-sm px-3 py-2 font-['Manrope']"
              />
              <input
                placeholder="Customer phone"
                value={form.customer_phone}
                onChange={(e) => setForm((p) => ({ ...p, customer_phone: e.target.value }))}
                className="bg-white/5 border border-white/10 text-white text-sm px-3 py-2 font-['Manrope']"
              />
              <input
                placeholder="Vehicle info"
                value={form.vehicle_info}
                onChange={(e) => setForm((p) => ({ ...p, vehicle_info: e.target.value }))}
                className="bg-white/5 border border-white/10 text-white text-sm px-3 py-2 font-['Manrope'] md:col-span-2"
              />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-['Manrope'] text-white/60 hover:text-white">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-['Manrope'] font-medium disabled:opacity-50"
            >
              {saving ? 'Saving…' : editEvent ? 'Update Event' : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}